import {sourceMediaRequest} from '@waka/compat/media-request';
import { BrowserRenderMediaAudio as MediaAudio } from '@waka/compat/browser-render-media';
import { useMemo } from 'react';
import { Html5Audio, useRemotionEnvironment, useVideoConfig } from 'remotion';
import { RequireCachedAsset } from '../../caching/require-cached-asset';
import {
  useAssetStartDelta,
  useIsInExtensionArea,
} from '../../canvas/transition-extension-context';
import { useIsGlobalContinuousAudioItem } from '../../canvas/global-continuous-audio-layer';
import { useIsGlobalTtsItem } from '../../canvas/global-tts-audio-layer';
import { useStablePreferredLocalUrl } from '../../utils/find-asset-by-id';
import { useAssetFromItem } from '../../utils/use-context';
import { volumeFn } from '../../utils/volume-fn';
import { AudioItem } from './audio-item-type';

export const AudioLayer = ({
  item,
  trackMuted,
  disableBoundaryMediaPause,
}: {
  item: AudioItem;
  trackMuted: boolean;
  disableBoundaryMediaPause?: boolean;
}) => {
  const { fps } = useVideoConfig();
  const { isClientSideRendering, isRendering } = useRemotionEnvironment();
  // Player는 기존 Html5Audio 재생을 유지하고, 렌더만 프레임 추출 방식으로 처리해
  // Lambda 청크 경계에서 AAC 오디오 시작점이 앞당겨지는 현상을 막는다.
  //
  const useRendererAudio =
    isClientSideRendering || (isRendering && item.playbackRate >= 1);

  // 트랜지션 확장 영역에서는 오디오 음소거
  const isInExtensionArea = useIsInExtensionArea();
  // target 확장으로 인한 assetStart 조정값
  const assetStartDelta = useAssetStartDelta();
  // 글로벌 TTS 레이어에서 연속 재생 중인 아이템은 per-scene 오디오 스킵
  const isGlobalTts = useIsGlobalTtsItem(item.id);
  const isGlobalContinuousAudio = useIsGlobalContinuousAudioItem(item.id);

  // target 확장이 있으면 assetStart를 앞으로 조정 (assetStartDelta는 음수)
  const startFromInFrames =
    (item.audioStartFromInSeconds + assetStartDelta) * fps;

  const volume = useMemo(() => {
    return volumeFn({
      fadeWindow:item.audioFadeWindow,
      fps,
      audioFadeInDurationInSeconds: item.audioFadeInDurationInSeconds,
      audioFadeOutDurationInSeconds: item.audioFadeOutDurationInSeconds,
      durationInFrames: item.durationInFrames,
      decibelAdjustment: item.decibelAdjustment,
    });
  }, [
    fps,
    item.audioFadeWindow,
    item.audioFadeInDurationInSeconds,
    item.audioFadeOutDurationInSeconds,
    item.durationInFrames,
    item.decibelAdjustment,
  ]);

  const asset = useAssetFromItem(item);
  const src = useStablePreferredLocalUrl(asset);

  if (isGlobalTts || isGlobalContinuousAudio) return null;

  return (
    <RequireCachedAsset asset={asset}>
      {useRendererAudio ? (
        <MediaAudio
          requestInit={sourceMediaRequest}
          trimBefore={startFromInFrames}
          src={src}
          volume={volume}
          playbackRate={item.playbackRate}
          muted={trackMuted || isInExtensionArea}
        />
      ) : (
        <Html5Audio
          crossOrigin="anonymous"
          pauseWhenBuffering={!disableBoundaryMediaPause}
          trimBefore={startFromInFrames}
          src={src}
          useWebAudioApi
          volume={volume}
          playbackRate={item.playbackRate}
          muted={trackMuted || isInExtensionArea}
        />
      )}
    </RequireCachedAsset>
  );
};
